// useCustomFontLoader — extrahiert aus app/src/components/editor/Viewer/
// useCustomFontLoader.ts (Plan A Mapping-Audit 2026-05-22).
//
// FontInfoDto-Typ wurde von app/-types/actions.ts in dieses Modul inline-
// kopiert um die app/-Coupling aufzulösen. Das Schema ist seit Phase 1
// stabil.

import type { CustomFonts, FontMapping } from "@scrolly/post-types";
import { useEffect, useMemo } from "react";

export interface FontInfoDto {
  familyName: string;
  weight: number;
  italic: boolean;
  variable: boolean;
  filename: string;
  fontUrl: string;
}

/**
 * Polyfill document.fonts.entries() for Firefox.
 * @see https://sidneyliebrand.io/blog/fixing-font-face-set-entries-not-iterable-in-firefox
 */
function fontFaceSetIteratorToArray(target: FontFaceSet) {
  const iterable = target.entries();
  const results: [FontFace, FontFace][] = [];
  let iterator = iterable.next();
  while (iterator.done === false) {
    results.push(iterator.value);
    iterator = iterable.next();
  }
  return results;
}

export default function useCustomFontLoader(
  fontInfos: FontInfoDto[] | undefined,
  fontMapping: FontMapping | undefined,
): CustomFonts {
  const textFont = fontMapping?.text;
  const headlineFont = fontMapping?.headline;
  useEffect(() => {
    if (fontInfos) {
      fontInfos.forEach((fi) => {
        if (
          [textFont?.family, headlineFont?.family].includes(fi.familyName)
        ) {
          const fontFace = new FontFace(
            fi.familyName,
            `url(${fi.fontUrl})`,
            {
              weight: String(fi.weight),
              style: fi.italic ? "italic" : "normal",
            },
          );
          if (
            !fontFaceSetIteratorToArray(document.fonts).some(
              (ff) => ff[0]?.family === fi.familyName,
            )
          ) {
            fontFace.load().then((loadedFace) => {
              document.fonts.add(loadedFace);
            });
          }
        }
      });
    }
  }, [fontInfos, headlineFont, textFont]);

  return useMemo(() => {
    return {
      textFontFamily: textFont?.family,
      headlineFontFamily: headlineFont?.family,
    };
  }, [headlineFont?.family, textFont?.family]);
}
